import type { Message as SavedMessage } from '../api/generated-api';
import type { Message } from '../state/chatTypes';
import { getLatestUserMessage } from './get-latest-user-message';

const isSameText = (
  first: string | undefined,
  second: string | undefined
): boolean => (first ?? '').trim() === (second ?? '').trim();

/** The questions in a conversation with the given text, oldest first. */
const questionsWithText = <T extends { role?: string; content?: string }>(
  messages: ReadonlyArray<T>,
  content: string | undefined
): T[] =>
  messages.filter(
    (message) => message.role === 'user' && isSameText(message.content, content)
  );

/**
 * Whether a question with this text repeats while a retry may have put the
 * chat in a different order than the saved conversation: one of those
 * questions was retried, or a retried answer landed out of place. Counting
 * repeats can't tell those turns apart then.
 */
const isReorderedRepeat = (
  local: ReadonlyArray<Message>,
  content: string | undefined
): boolean => {
  const repeats = questionsWithText(local, content);
  if (repeats.length < 2) {
    return false;
  }
  return (
    repeats.some(({ wasRetried }) => wasRetried === true) ||
    local.some(
      (message, index) =>
        message.role === 'assistant' &&
        message.sourceQuestionId !== undefined &&
        getLatestUserMessage(local.slice(0, index))?.id !==
          message.sourceQuestionId
    )
  );
};

/**
 * Finds the saved copy of the latest question. An edit only replaces the
 * latest question, so its saved copy must be the latest saved question with
 * the same text. When the chat and the saved conversation hold a different
 * number of questions with that text, such as a repeat of a question that
 * was stopped before it was sent, the turn can't be told apart safely.
 * @param saved - The saved conversation, oldest first.
 * @param local - The local conversation, oldest first.
 * @param content - The text of the latest local question.
 * @returns The saved question, or `undefined` when it can't be identified.
 */
export const findSavedLatestQuestion = (
  saved: ReadonlyArray<SavedMessage>,
  local: ReadonlyArray<Message>,
  content: string
): SavedMessage | undefined => {
  const savedQuestions = saved.filter(({ role }) => role === 'user');
  const latest = savedQuestions[savedQuestions.length - 1];
  const isSameCount =
    questionsWithText(saved, content).length ===
    questionsWithText(local, content).length;
  return latest !== undefined &&
    isSameText(latest.content, content) &&
    isSameCount &&
    !isReorderedRepeat(local, content)
    ? latest
    : undefined;
};

/**
 * Finds the saved copy of a local answer through the question before it.
 * Both lists keep questions in the order they were asked, so the nth local
 * question with some text matches the nth saved question with that text,
 * as long as both hold the same number of them and the question has a
 * single saved answer.
 * @param saved - The saved conversation, oldest first.
 * @param local - The local conversation, oldest first.
 * @param answerId - The local id of the answer.
 * @returns The saved answer, or `undefined` when it cannot be matched.
 */
export const findSavedAnswer = (
  saved: ReadonlyArray<SavedMessage>,
  local: ReadonlyArray<Message>,
  answerId: string
): SavedMessage | undefined => {
  const answerIndex = local.findIndex(({ id }) => id === answerId);
  const sourceQuestionId = local[answerIndex]?.sourceQuestionId;
  // An answer stamped with its question uses that. Otherwise it is matched
  // by position, which only holds when it is the first answer after the
  // question, since a retried question's answer lands after later turns.
  const question =
    sourceQuestionId === undefined
      ? getLatestUserMessage(local.slice(0, Math.max(answerIndex, 0)))
      : local.find(({ id }) => id === sourceQuestionId);
  if (question === undefined) {
    return undefined;
  }
  const isFirstAnswer = local
    .slice(local.indexOf(question) + 1, answerIndex)
    .every(({ role }) => role !== 'assistant');
  if (
    (sourceQuestionId === undefined && !isFirstAnswer) ||
    isReorderedRepeat(local, question.content)
  ) {
    return undefined;
  }
  const localQuestions = questionsWithText(local, question.content);
  const savedQuestions = questionsWithText(saved, question.content);
  const savedQuestion =
    localQuestions.length === savedQuestions.length
      ? savedQuestions[localQuestions.indexOf(question)]
      : undefined;
  if (savedQuestion?.id === undefined) {
    return undefined;
  }
  // More than one saved answer to the question can't be told apart either.
  const savedAnswers = saved.filter(
    ({ role, parentId }) =>
      role === 'assistant' && parentId === savedQuestion.id
  );
  return savedAnswers.length === 1 ? savedAnswers[0] : undefined;
};
